import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, ObservedValuesFromArray, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { BookedFlightActionEnum } from '@enums/booked-flight-action.enum';
import { TextRemarks } from '@services/content/_interfaces_/content.mapped.interface';
import { FilterCriteria } from '@shared/services/flight/_interfaces_/flight.interface';
import { UrlEnum } from 'src/app/core/enums/url.enum';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { BookedOrderActionInfoMapped } from '../order/_interfaces_/booked-order.mapped.interface';
import { UrlService } from '../url/url.service';
import { TicketConditions, MiniRule, TicketConditionsFullResponse } from './_interfaces_/booked-flight-ticket-conditions.interface';
import { BookedFlightTicketConditionMapped } from './_interfaces_/booked-flight-ticket-conditions.mapped.interface';
import { BookedFlight, RetrivedFlightLinkResponse } from './_interfaces_/booked-flight.interface';
import { BookedFlightMapped } from './_interfaces_/booked-flight.mapped.interface';
import { FlightMapperService } from './flight-mapper.service';
import { FlightSplitInfo, UpdateFlightSplitRequest } from './_interfaces_/split-pnr.interface';

@Injectable({
  providedIn: 'root',
})
export class FlightService {
  public bookedFlight: BookedFlight;
  constructor(
    @Inject(HttpClient) private http: HttpClient,
    @Inject(UrlService) private urlService: UrlService,
    @Inject(FlightMapperService) private mapperService: FlightMapperService,
    @Inject(LocalStorageService) private localStorageService: LocalStorageService
  ) {}

  public getMappedBookedFlight(bookedFlight: BookedFlight): Observable<BookedFlightMapped> {
    return of(this.mapperService.mapBookedFlight(bookedFlight));
  }

  public putBookedFlight(id: string): Observable<BookedFlightMapped> {
    const url = `${this.urlService.urls[UrlEnum.VALIDATE_OPTION_ORDER]}?bookedFlightId=${id}`;
    const headers = { SessionId: this.localStorageService.getUserSessionToken() };
    return this.http.get<BookedFlight>(url, { headers }).pipe(map((bookedFlight) => this.mapperService.mapBookedFlight(bookedFlight)));
  }

  public getTicketConditions(id: string): Observable<BookedFlightTicketConditionMapped[]> {
    const headers = { SessionId: this.localStorageService.getUserSessionToken() };
    const url: string = `${this.urlService.urls[UrlEnum.BOOKED_FLIGHT_TICKET_CONDITIONS]}?flightId=${id}`;
    return this.http.get<TicketConditions>(url, { headers }).pipe(map((conditions) => this.mapperService.mapTicketConditions(conditions)));
  }

  public getTicketConditionsWithMiniRules(
    id: string
  ): Observable<{ ticketConditions: BookedFlightTicketConditionMapped[]; miniRules: MiniRule[] }> {
    const headers = { SessionId: this.localStorageService.getUserSessionToken() };
    const url: string = `${this.urlService.urls[UrlEnum.BOOKED_FLIGHT_TICKET_CONDITIONS]}?flightId=${id}`;

    return this.http.get<TicketConditionsFullResponse>(url, { headers }).pipe(
      map((response) => {
        return {
          ticketConditions: response.ticketConditionList
            ? this.mapperService.mapTicketConditions({
                _links: response._links,
                selectedFlightIdentifier: response.selectedFlightIdentifier,
                ticketConditionList: response.ticketConditionList,
              })
            : [],
          miniRules: response.miniRuleList || [],
        };
      })
    );
  }

  public postCancelSelectedFlight(flightIdentifier: string): Observable<RetrivedFlightLinkResponse> {
    const url = this.urlService.urls[UrlEnum.CANCEL_FLIGHT];
    const headers = { SessionId: this.localStorageService.getUserSessionToken() };
    const payload = {
      flightIdentifier: flightIdentifier,
    };
    return this.http.post<RetrivedFlightLinkResponse>(url, payload, { headers });
  }

  public mapTextRemarks(flight: BookedFlightMapped, flightContent: Array<TextRemarks>): string[] {
    if (!flight.textRemarks) {
      return null;
    }
    const textRemarkKeys: any[] = flight.textRemarks;

    return flightContent.filter((textRemark) => textRemarkKeys.includes(textRemark.code)).map(({ textRemark }) => textRemark);
  }

  public getSplitInfo(flightIdentifier: string): Observable<FlightSplitInfo> {
    const culture = this.localStorageService.getCulture();
    const url = `${this.urlService.urls[UrlEnum.SPLIT_PNR]}?flightIdentifier=${flightIdentifier}&culture=${culture}`;
    const headers = { SessionId: this.localStorageService.getUserSessionToken() };

    return this.http.get<FlightSplitInfo>(url, { headers });
  }

  public updateSplitPassengerSelection(flightIdentifier: string, request: UpdateFlightSplitRequest): Observable<FlightSplitInfo> {
    const url = `${this.urlService.urls[UrlEnum.SPLIT_PNR]}/${flightIdentifier}`;
    const headers = { SessionId: this.localStorageService.getUserSessionToken() };
    request = {
      ...request,
      culture: this.localStorageService.getCulture(),
    };

    return this.http.put<FlightSplitInfo>(url, request, { headers });
  }

  public confirmSplitPnr(flightIdentifier: string): Observable<void> {
    const url = `${this.urlService.urls[UrlEnum.FLIGHT_CONFIRMATION]}`;
    const headers = { SessionId: this.localStorageService.getUserSessionToken() };
    const payload = {
      changingFlightIdentifier: flightIdentifier,
    };
    return this.http.post<void>(url, payload, { headers });
  }
}
